/* eslint-disable max-lines */
import { call, put, takeLatest } from "redux-saga/effects";
import {
  getChatBotConfigApi,
  getMyOrdersApi,
  getOrderPaymentPreferenceStatusApi,
  getPartnerLoyaltyContentIdDetailsApi,
  getPromotionalCommunicationApi,
  getSenderMobileCountryCodeAndCityApi,
  getCustomerProfileDetailsApi,
  updateCustomerProfileApi,
  setPromotionalCommunicationApi,
  getPartnersListApi,
  getWalletStatementApi,
  getGiftCouponsApi,
  deleteExpiredProductApi,
  deleteConfirmedOrderApi,
} from "../../../api/myAccount";
import { sendMobileOtp, verifyMobileOtp } from "../../../api/deliveryAddressApi";
import { getCallChatTime } from "../../../api/common";
import { SENDER_MOBILE_OTP } from "../../../constants/pageConstants/deliveryAddressPageConstant";
import {
  failedToCheckPaymentStatus,
  failedToFetchMyOrders,
  failedToFetchPartnerLoyaltyDetails,
  requestToCheckPaymentStatus,
  requestToFetchChatBotConfig,
  requestToFetchCallChatTime,
  requestToFetchMyOrders,
  requestToFetchPartnerLoyaltyDetails,
  responseFromCheckPaymentStatus,
  responseFromFetchChatBotConfig,
  responseFromFetchCallChatTime,
  failedToFetchCallChatTime,
  responseFromFetchMyOrders,
  responseFromFetchNotificationSettings,
  failedToFetchNotificationSettings,
  responseFromFetchPartnersList,
  requestToFetchNotificationSettings,
  requestToFetchSenderCountry,
  responseFromFetchPartnerLoyaltyDetails,
  setSenderCountryData,
  requestToFetchCustomerProfile,
  responseFromFetchCustomerProfile,
  failedToFetchCustomerProfile,
  requestToUpdateCustomerProfile,
  responseFromUpdateCustomerProfile,
  failedToUpdateCustomerProfile,
  requestProfileSendMobileOtp,
  profileMobileOtpSent,
  requestProfileVerifyMobileOtp,
  profileMobileOtpVerified,
  profileMobileOtpError,
  setProfileMobileValidated,
  requestToSaveNotificationSettings,
  responseFromSaveNotificationSettings,
  failedToSaveNotificationSettings,
  requestToFetchPartnersList,
  failedToFetchPartnersList,
  requestToFetchWalletStatement,
  responseFromFetchWalletStatement,
  failedToFetchWalletStatement,
  requestToFetchGiftVouchers,
  responseFromFetchGiftVouchers,
  failedToFetchGiftVouchers,
  requestToDeleteExpiredOrder,
  requestToDeleteDeliveredOrder,
  responseFromOrderDelete,
} from "../../slices/myAccount";

/**
 * @param {object} res
 * @returns {object}
 */
function parseNotificationPreferences(res) {
  if (res?.preferences && typeof res.preferences === "object") {
    return res.preferences;
  }
  const raw = res?.result ?? res?.data?.result;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.preferences) {
        return parsed.preferences;
      }
    } catch {
      // ignore parse errors
    }
  }
  if (res?.data?.preferences) {
    return res.data.preferences;
  }
  if (res?.data && typeof res.data === "object" && !Array.isArray(res.data)) {
    return res.data;
  }
  return {};
}

/**
 * @generator
 */
function* fetchMyOrdersSaga() {
  try {
    const res = yield call(getMyOrdersApi);
    if (res?.isSuccess) {
      yield put(responseFromFetchMyOrders(res));
    } else {
      yield put(failedToFetchMyOrders());
    }
  } catch {
    yield put(failedToFetchMyOrders());
  }
}

/**
 * @param {{ payload?: { orderId?: string } }} action
 */
function* checkPaymentStatusSaga(action) {
  try {
    const orderId = action.payload?.orderId;
    const res = yield call(getOrderPaymentPreferenceStatusApi, orderId);
    yield put(responseFromCheckPaymentStatus({ ...res, ...action.payload }));
  } catch {
    yield put(failedToCheckPaymentStatus());
  }
}

/**
 * @param {{ payload?: { subOrderId?: string } }} action
 */
function* fetchSenderCountrySaga(action) {
  try {
    const subOrderId = action.payload?.subOrderId;
    const res = yield call(getSenderMobileCountryCodeAndCityApi, subOrderId);
    if (res?.isSuccess) {
      yield put(setSenderCountryData(res?.data ?? res));
    }
  } catch {
    // non-blocking
  }
}

/**
 * @generator
 */
function* fetchPartnerLoyaltySaga() {
  try {
    const res = yield call(getPartnerLoyaltyContentIdDetailsApi);
    yield put(responseFromFetchPartnerLoyaltyDetails(res?.data ?? res));
  } catch {
    yield put(failedToFetchPartnerLoyaltyDetails());
  }
}

/**
 * @generator
 */
function* fetchChatBotConfigSaga() {
  try {
    const res = yield call(getChatBotConfigApi);
    yield put(responseFromFetchChatBotConfig(res));
  } catch {
    yield put(responseFromFetchChatBotConfig({}));
  }
}

/**
 * @generator
 */
function* fetchCallChatTimeSaga() {
  try {
    const res = yield call(getCallChatTime);
    yield put(responseFromFetchCallChatTime(res ?? {}));
  } catch {
    yield put(failedToFetchCallChatTime());
  }
}

/**
 * @generator
 */
function* fetchNotificationSettingsSaga() {
  try {
    const res = yield call(getPromotionalCommunicationApi);
    const preferences = parseNotificationPreferences(res);
    if (Object.keys(preferences).length > 0 || res?.isSuccess) {
      yield put(responseFromFetchNotificationSettings(preferences));
    } else {
      yield put(failedToFetchNotificationSettings());
    }
  } catch {
    yield put(failedToFetchNotificationSettings());
  }
}

/**
 * @param {{ payload?: Record<string, boolean> }} action
 */
function* saveNotificationSettingsSaga(action) {
  try {
    const res = yield call(setPromotionalCommunicationApi, action.payload ?? {});
    if (res?.isSuccess || res?.data?.isSuccess) {
      yield put(responseFromSaveNotificationSettings(action.payload ?? {}));
    } else {
      yield put(failedToSaveNotificationSettings());
    }
  } catch {
    yield put(failedToSaveNotificationSettings());
  }
}

/**
 * @generator
 */
function* fetchPartnersListSaga() {
  try {
    const res = yield call(getPartnersListApi);
    const data = Array.isArray(res) ? res : (res?.data ?? res?.content ?? []);
    yield put(responseFromFetchPartnersList(data));
  } catch {
    yield put(failedToFetchPartnersList());
  }
}

/**
 * @param {{ payload?: { page?: number, append?: boolean } }} action
 */
function* fetchWalletStatementSaga(action) {
  try {
    const page = action.payload?.page ?? 1;
    const res = yield call(getWalletStatementApi, page);
    const response = res?.response ?? res?.data?.response ?? res?.data ?? {};
    if (res?.isSuccess === true || res?.data?.isSuccess === true || response?.statements) {
      yield put(
        responseFromFetchWalletStatement({
          statements: response?.statements ?? [],
          totalCount: response?.totalCount ?? 0,
          page,
          append: Boolean(action.payload?.append),
        }),
      );
    } else {
      yield put(failedToFetchWalletStatement());
    }
  } catch {
    yield put(failedToFetchWalletStatement());
  }
}

/**
 * @generator
 */
function* fetchGiftVouchersSaga() {
  try {
    const res = yield call(getGiftCouponsApi);
    const data = res?.data ?? res;
    yield put(
      responseFromFetchGiftVouchers({
        unused: data?.relevantUnUsedCouponsList ?? [],
        used: data?.relevantUsedCouponsList ?? [],
        currencies: data?.currencies ?? [],
      }),
    );
  } catch {
    yield put(failedToFetchGiftVouchers());
  }
}

/**
 * @generator
 */
function* fetchCustomerProfileSaga() {
  try {
    const res = yield call(getCustomerProfileDetailsApi);
    if (res?.data?.isSuccess || res?.isSuccess) {
      const payload = res?.data ?? res;
      yield put(responseFromFetchCustomerProfile(payload));
    } else {
      yield put(failedToFetchCustomerProfile());
    }
  } catch {
    yield put(failedToFetchCustomerProfile());
  }
}

/**
 * @param {{ payload?: Record<string, string> }} action
 */
function* updateCustomerProfileSaga(action) {
  try {
    const res = yield call(updateCustomerProfileApi, action.payload ?? {});
    const data = res?.data ?? res;
    if (data?.isSuccess === "success" || data?.isSuccess === true) {
      yield put(
        responseFromUpdateCustomerProfile({
          customerName: action.payload?.name,
          emailAddress: action.payload?.email,
          phoneNum: action.payload?.phone,
          countryCode: action.payload?.countryCode,
          gender: action.payload?.gender,
          address: action.payload?.address,
          postalCode: action.payload?.postalcode,
          city: action.payload?.city,
          countryGeoId: action.payload?.countryGeoId,
          isMobileValidated: Boolean(action.payload?.isMobileValidated),
        }),
      );
    } else {
      yield put(failedToUpdateCustomerProfile());
    }
  } catch {
    yield put(failedToUpdateCustomerProfile());
  }
}

/**
 * @param {{ payload?: { mobileNumber?: string, countryCode?: string, deviceType?: string } }} action
 */
function* profileSendMobileOtpSaga(action) {
  try {
    const formData = new URLSearchParams();
    formData.append("MOBILENUMBER", action.payload?.mobileNumber ?? "");
    formData.append("countryCode", action.payload?.countryCode ?? "");
    formData.append("deviceType", action.payload?.deviceType ?? "desktop");
    const response = yield call(sendMobileOtp, formData);
    if (response?.responseStatus === "success" || response?.response === "success") {
      yield put(profileMobileOtpSent());
    } else {
      yield put(profileMobileOtpError(response?.errorMessage || response?.msg || SENDER_MOBILE_OTP.sendOtpError));
    }
  } catch {
    yield put(profileMobileOtpError(SENDER_MOBILE_OTP.sendOtpError));
  }
}

/**
 * @param {{ payload?: { mobileNumber?: string, countryCode?: string, otp?: string, username?: string } }} action
 */
function* profileVerifyMobileOtpSaga(action) {
  try {
    const formData = new URLSearchParams();
    formData.append("MOBILENUMBER", action.payload?.mobileNumber ?? "");
    formData.append("COUNTRYCODE", `${action.payload?.countryCode ?? ""}`);
    formData.append("OTP", action.payload?.otp ?? "");
    if (action.payload?.username) {
      formData.append("USERNAME", action.payload.username);
    }
    const response = yield call(verifyMobileOtp, formData);
    if (response?.response === "success") {
      yield put(profileMobileOtpVerified());
      yield put(
        setProfileMobileValidated({
          phoneNum: action.payload?.mobileNumber,
          countryCode: action.payload?.countryCode,
        }),
      );
    } else {
      yield put(profileMobileOtpError(response?.msg || SENDER_MOBILE_OTP.verifyOtpError));
    }
  } catch {
    yield put(profileMobileOtpError(SENDER_MOBILE_OTP.verifyOtpError));
  }
}

/**
 * @param {{ payload: string }} action
 */
function* deleteExpiredOrderSaga(action) {
  try {
    const res = yield call(deleteExpiredProductApi, action.payload);
    if (res?.responseMessage === "success") {
      yield put(responseFromOrderDelete());
      yield put(requestToFetchMyOrders());
    }
  } catch {
    // ignore
  }
}

/**
 * @param {{ payload: string }} action
 */
function* deleteDeliveredOrderSaga(action) {
  try {
    const res = yield call(deleteConfirmedOrderApi, action.payload);
    if (res?.responseStatus === "success") {
      yield put(responseFromOrderDelete());
      yield put(requestToFetchMyOrders());
    }
  } catch {
    // ignore
  }
}

/**
 * @generator
 */
export default function* watchMyAccountSaga() {
  yield takeLatest(requestToFetchMyOrders.type, fetchMyOrdersSaga);
  yield takeLatest(requestToCheckPaymentStatus.type, checkPaymentStatusSaga);
  yield takeLatest(requestToFetchPartnerLoyaltyDetails.type, fetchPartnerLoyaltySaga);
  yield takeLatest(requestToFetchChatBotConfig.type, fetchChatBotConfigSaga);
  yield takeLatest(requestToFetchCallChatTime.type, fetchCallChatTimeSaga);
  yield takeLatest(requestToFetchNotificationSettings.type, fetchNotificationSettingsSaga);
  yield takeLatest(requestToFetchSenderCountry.type, fetchSenderCountrySaga);
  yield takeLatest(requestToFetchCustomerProfile.type, fetchCustomerProfileSaga);
  yield takeLatest(requestToUpdateCustomerProfile.type, updateCustomerProfileSaga);
  yield takeLatest(requestProfileSendMobileOtp.type, profileSendMobileOtpSaga);
  yield takeLatest(requestProfileVerifyMobileOtp.type, profileVerifyMobileOtpSaga);
  yield takeLatest(requestToSaveNotificationSettings.type, saveNotificationSettingsSaga);
  yield takeLatest(requestToFetchPartnersList.type, fetchPartnersListSaga);
  yield takeLatest(requestToFetchWalletStatement.type, fetchWalletStatementSaga);
  yield takeLatest(requestToFetchGiftVouchers.type, fetchGiftVouchersSaga);
  yield takeLatest(requestToDeleteExpiredOrder.type, deleteExpiredOrderSaga);
  yield takeLatest(requestToDeleteDeliveredOrder.type, deleteDeliveredOrderSaga);
}
